import { Injectable } from '@angular/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { TrackedDay } from './DataModels/TrackedDay';
import { UserChangedEvent, UserChangeType } from './DataModels/User';
import { sameDay } from './helpers/helpers';
import { UserService } from './user.service';
import { MealType } from 'src/app/DataModels/Meal';
import {PlatformHelperService} from './helpers/platform-helper.service';

const breakfastPrefixCode = 1;
const lunchPrefixCode = 2;
const dinnerPrefixCode = 3;

@Injectable({
  providedIn: 'root'
})
export class LocalNotificationsService {

  private dailyRemindersEnabled = false;
  private breakfastReminderEnabled = false;
  private lunchReminderEnabled = false;
  private dinnerReminderEnabled = false;
  private breakfastTime: Date;
  private lunchTime: Date;
  private dinnerTime: Date;

  private localNotificationsSupported = true;

  private readonly reminderHour: number = 20;

  private readonly isAndroid: boolean;


  constructor(private userService: UserService, private platform: PlatformHelperService) {
    this.subscribeToUserChanges();
    this.userService.newUserAssignedSubject.subscribe(event => {
      if (event) { this.subscribeToUserChanges(); }
    });
    this.isAndroid = this.platform.isAndroid;

  }

  private mealName(mealType: MealType): string{
    switch (mealType){
      case MealType.Breakfast: return 'Frühstück';
      case MealType.Lunch: return 'Mittagessen';
      case MealType.Dinner: return 'Abendessen';
    }
  }

  async checkDailyReminders(){
    // Leave method if reminders are disabled or local notifications are not supported
    if (!this.dailyRemindersEnabled || !this.localNotificationsSupported) { return; }
    const count = 7; // 7 days of empty trackedDay reminders added

    // Check for today first (separate checking necessary as entries could be there already)
    if (this.userService.user && this.userService.user.trackedDays){
      const trackedDay = this.userService.user.trackedDays[this.userService.user.getTrackedDay(new Date())];
      this.updateRemindersForToday(trackedDay).then();
    }

    // Initialize variables for future notifications
    const day: Date = new Date();
    day.setDate(day.getDate() + 1);
    day.setHours(this.reminderHour, 0, 0, 0);

    // add or overwrite all notifications for next **count** days
    for (let i = 1; i < count; i++){
      // console.log("reminder "+i+", ID: "+time+" date: "+day);
      this.scheduleDailyReminder(day);
      day.setDate(day.getDate() + 1);
    }
    // await LocalNotifications.getIds().then(id => {
    //   console.log ("All scheduled Notification IDs " + JSON.stringify(id));
    // })
  }

  async checkMealReminders(){
    // Leave method if reminders are disabled or local notifications are not supported

    if (!this.localNotificationsSupported) { return; }
    const count = 7; // 7 days of empty trackedDay reminders added
    // Initialize variables for future notifications
    const breakfast: Date = new Date();
    const lunch: Date = new Date();
    const dinner: Date = new Date();
    console.log(this.userService.user.notificationSettings);
    breakfast.setHours(this.userService.user.notificationSettings.breakfastTime.getHours(),
        this.userService.user.notificationSettings.breakfastTime.getMinutes(), 0, 0);
    lunch.setHours(this.userService.user.notificationSettings.lunchTime.getHours(),
        this.userService.user.notificationSettings.lunchTime.getMinutes(), 0, 0);
    dinner.setHours(this.userService.user.notificationSettings.dinnerTime.getHours(),
        this.userService.user.notificationSettings.dinnerTime.getMinutes(), 0, 0);
    // add or overwrite all notifications for next **count** days
    for (let i = 1; i < count; i++){
      if (this.userService.user.notificationSettings.breakfastTrackerReminder &&
          !this.userService.user.typicalMeals[MealType.Breakfast].disabled) {
        this.scheduleMealReminder(breakfast, MealType.Breakfast);
      }
      if (this.userService.user.notificationSettings.lunchTrackerReminder && !this.userService.user.typicalMeals[MealType.Lunch].disabled) {
        this.scheduleMealReminder(lunch, MealType.Lunch);
      }
      if (this.userService.user.notificationSettings.dinnerTrackerReminder &&
          !this.userService.user.typicalMeals[MealType.Dinner].disabled) {
        this.scheduleMealReminder(dinner, MealType.Dinner);
      }

      breakfast.setDate(breakfast.getDate() + 1);
      lunch.setDate(lunch.getDate() + 1);
      dinner.setDate(dinner.getDate() + 1);
    }
    await LocalNotifications.getPending().then( res => {
      console.log(res);
    });
  }

  // Method is called on every tracked-day change and removes/adds the reminder notifications
  private async updateRemindersForToday(trackedDay: TrackedDay): Promise<void> {
    // Leave method if reminders are disabled or local notifications are not supported
    if (!this.localNotificationsSupported) { return; }
    // Leave method if change wasn't for this day
    if (trackedDay && !sameDay(new Date(), trackedDay.day)) { return; }
    // Initialize time variables
    const day =  new Date();  // date.getTime() used for notificationID
    day.setHours(this.reminderHour, 0, 0, 0);
    // delete today's notification if any emissions logged for today (easiest method)
    if (trackedDay.emissions !== 0){
      console.log('Cancelling reminder for ' + day);
      // await LocalNotifications.cancel(time);
      this.cancelNotificationByID(this.calculateNotificationId(day));
    }
    else{
       // create/overwrite today's notification in other case
      console.log('Creating/overwriting reminder for ' + day);
      this.scheduleDailyReminder(day);
      this.showPendingNotifications();
    }

      // check if meals were edited, cancel those reminders if so
    trackedDay.meals.forEach(meal => {
        console.log(meal);
        if (meal.type === MealType.Breakfast && meal.edited){
          day.setHours(this.userService.user.notificationSettings.breakfastTime.getHours());
          day.setMinutes(this.userService.user.notificationSettings.breakfastTime.getMinutes());
          const id = this.calculateNotificationId(day, breakfastPrefixCode);
          console.log('cancelling breakfast reminder for today, ID: ' + id);
          this.cancelNotificationByID(id);
        }
        if (meal.type === MealType.Lunch && meal.edited){
          day.setHours(this.userService.user.notificationSettings.lunchTime.getHours());
          day.setMinutes(this.userService.user.notificationSettings.lunchTime.getMinutes());
          const id = this.calculateNotificationId(day, lunchPrefixCode);
          console.log('cancelling lunch reminder for today, ID: ' + id);
          this.cancelNotificationByID(id);
        }
        if (meal.type === MealType.Dinner && meal.edited){
          day.setHours(this.userService.user.notificationSettings.dinnerTime.getHours());
          day.setMinutes(this.userService.user.notificationSettings.dinnerTime.getMinutes());
          const id = this.calculateNotificationId(day, dinnerPrefixCode);
          console.log('cancelling dinner reminder for today, ID: ' + id);
          this.cancelNotificationByID(id);
        }
      });
  }

  private scheduleDailyReminder(time: Date): void {
    const now: number = new Date().getTime();
    if (now < time.getTime()){
      LocalNotifications.requestPermissions().then();
      LocalNotifications.schedule({
        notifications: [
          {
            id: this.calculateNotificationId(time), // notification IDs are the dates
            title: 'Erinnerung',
            body: 'Du hast heute noch nichts eingegeben! Hier klicken um das zu ändern.',
            schedule: { at: new Date(time.getTime()) },
            smallIcon: this.isAndroid ? 'idoplanty_status_bar_logo' : undefined
          }
        ]

      }).then();
    }else{
      console.log('not scheduling daily reminder ' +  this.calculateNotificationId(time) + ': scheduled before current time');
    }
  }

  private showPendingNotifications(){
    console.log('current pending Notifications: ' + LocalNotifications.getPending().then(res => {
      console.log(res);
    }));
  }

  private scheduleMealReminder(time: Date, meal: MealType): void {
    const now: number = new Date().getTime();
    console.log('scheduling reminder for ' + this.mealName(meal));
    let mealPrefixCode: number;
    switch (meal){
      case MealType.Breakfast: mealPrefixCode = breakfastPrefixCode; break;
      case MealType.Lunch: mealPrefixCode = lunchPrefixCode; break;
      case MealType.Dinner: mealPrefixCode = dinnerPrefixCode; break;
    }
    if (now < time.getTime()){
      LocalNotifications.requestPermissions().then();
      LocalNotifications.schedule({
        notifications: [
          {
            // id: Number(mealPrefixCode.toString() + time.toString()),
            id: this.calculateNotificationId(time, mealPrefixCode),
            title: this.mealName(meal) + ' Erinnerung',
            body: 'Du hast für dein ' + this.mealName(meal) + ' noch nichts eingegeben! Hier klicken um das zu ändern.',
            schedule: { at: new Date(time.getTime()) },
            smallIcon: this.isAndroid ? 'idoplanty_status_bar_logo' : undefined
          }
        ]
      }).then();
    }
    else{
      console.log('not scheduling meal reminder ' +  this.calculateNotificationId(time, mealPrefixCode)
          + ': scheduled before current time');
    }
  }

  public async scheduleTestReminder(time: Date) {
    console.log('scheduling test reminder');
    LocalNotifications.requestPermissions().then();
    const notifs = await LocalNotifications.schedule({
      notifications: [
        {
          id: this.calculateNotificationId(time), // notification IDs are the dates
          title: 'test title',
          body: 'test message',
          schedule: { at: new Date(time.getTime()) },
          smallIcon: this.isAndroid ? 'idoplanty_status_bar_logo' : undefined
        }
      ]

    });
    console.log(notifs);
  }

  private subscribeToUserChanges(): void {
    if (!this.userService.user) { return; }
    this.dailyRemindersEnabled = this.userService.user.notificationSettings.trackerReminder;
    this.checkDailyReminders().then();
    this.userService.user.changedSubject.subscribe((event: UserChangedEvent) => {
      if (event) {
        this.onUserChangedEvent(event);
      }
    });
  }

  private cancelNotificationByID(NID: number){
    LocalNotifications.getPending().then( res => {
      res.notifications = res.notifications.filter(i => i.id === NID);
      if (res.notifications.length > 0){
          console.log('cancelling notification for ' + NID);
          LocalNotifications.cancel(res).then();
        }
        else{
          console.log('notification ID ' + NID + ' not found; cancel unsuccessful');
        }
      }, err => {
        console.log(err);
      });
  }

  private cancelAllNotifications(){
    LocalNotifications.getPending().then( res => {
        if (res.notifications.length > 0){
          LocalNotifications.cancel(res).then();
        }
        else{
          console.log('notification length already zero, cancel all unnecessary');
        }
      }, err => {
        console.log(err);
      });
  }

  private onUserChangedEvent(event: UserChangedEvent): void {

    // If something was changed on a tracked day, update the tracker reminders
    if (event.type === UserChangeType.added_tracked_day || event.type === UserChangeType.changed_tracked_day){
      if (!(event.value instanceof TrackedDay)) { return; }
      this.updateRemindersForToday(event.value).then();
      return;
    }
    // If notification settings were changed
    if (event.type === UserChangeType.changed_notificaton_settings) {
      if (this.dailyRemindersEnabled === this.userService.user.notificationSettings.trackerReminder
          && this.breakfastReminderEnabled === this.userService.user.notificationSettings.breakfastTrackerReminder
          && this.lunchReminderEnabled === this.userService.user.notificationSettings.lunchTrackerReminder
          && this.dinnerReminderEnabled === this.userService.user.notificationSettings.dinnerTrackerReminder
          && this.breakfastTime === this.userService.user.notificationSettings.breakfastTime
          && this.lunchTime === this.userService.user.notificationSettings.lunchTime
          && this.dinnerTime === this.userService.user.notificationSettings.dinnerTime
          ) { return; }
      this.dailyRemindersEnabled = this.userService.user.notificationSettings.trackerReminder;
      this.breakfastReminderEnabled = this.userService.user.notificationSettings.breakfastTrackerReminder;
      this.lunchReminderEnabled = this.userService.user.notificationSettings.lunchTrackerReminder;
      this.dinnerReminderEnabled = this.userService.user.notificationSettings.dinnerTrackerReminder;
      this.breakfastTime = this.userService.user.notificationSettings.breakfastTime;
      this.lunchTime = this.userService.user.notificationSettings.lunchTime;
      this.dinnerTime = this.userService.user.notificationSettings.dinnerTime;

      this.cancelAllNotifications(); // first remove all notifications first in case user changed time for meals
      this.checkDailyReminders().then();  // generate new daily reminders
      this.checkMealReminders().then();  // generate new mealtime reminders

      console.log('notifications after update: ');
      this.showPendingNotifications();
      return;
    }
  }

  private calculateNotificationId(date: Date, code?: number | undefined): number {
    let id = date.getTime() / 1000 / 60; // Normalizes the time by milliseconds and seconds
    if (code) {
      id = Number(String(code) + String(id));
    }
    console.log(`Calculated id ${id} from date ${date.toLocaleString()}` + code ? ` with code ${code}` : '');
    return id;
  }
}
